import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectSubscriptions,
    selectProducts,
    selectLoading,
    selectError
} from '../../store/Subscriptions/selectors';
import {
    fetchSubscriptionsThunk,
    createSubscriptionThunk,
    cancelSubscriptionThunk,
    renameSubscriptionThunk,
    regenerateKeyThunk
} from '../../store/Subscriptions/thunks';
import { setError as clearSubscriptionError, setEnvironment as setSubscriptionsEnvironment } from '../../store/Subscriptions/reducer';
import { setApiEnvironment } from '../../api/apiClient';
import SubscriptionCard from './SubscriptionCard';
import CreateSubscriptionForm from './CreateSubscriptionForm';

interface SubscriptionManagerProps {
    userId: string;
    environment: 'sandbox' | 'production';
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({ userId, environment }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Redux state
    const subscriptions = useSelector(selectSubscriptions);
    const products = useSelector(selectProducts);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);

    const [searchQuery, setSearchQuery] = useState('');
    const subscriptionLimit = 2;

    // Clear Redux error silently (no snackbar)
    useEffect(() => {
        if (error) {
            dispatch(clearSubscriptionError(null));
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (userId) {
            setApiEnvironment(environment);
            dispatch(setSubscriptionsEnvironment(environment));
            dispatch(fetchSubscriptionsThunk(userId) as any);
        }
    }, [dispatch, userId, environment]);

    const handleCreateSubscription = async (productId: string, displayName: string) => {
        try {
            await dispatch(createSubscriptionThunk(userId, productId, displayName) as any);
        } catch (err: any) {
            console.error('Failed to create subscription:', err);
            throw err;
        }
    };

    const handleCancelSubscription = async (subscriptionId: string) => {
        try {
            await dispatch(cancelSubscriptionThunk(subscriptionId) as any);
        } catch (err: any) {
            console.error('Failed to cancel subscription:', err);
        }
    };

    const handleRenameSubscription = async (subscriptionId: string, newName: string) => {
        try {
            await dispatch(renameSubscriptionThunk(subscriptionId, newName) as any);
        } catch (err: any) {
            console.error('Failed to rename subscription:', err);
        }
    };

    const handleRegenerateKey = async (subscriptionId: string, keyType: 'primary' | 'secondary') => {
        try {
            await dispatch(regenerateKeyThunk(subscriptionId, keyType) as any);
        } catch (err: any) {
            console.error('Failed to regenerate key:', err);
        }
    };

    const handleRetry = () => {
        dispatch(fetchSubscriptionsThunk(userId) as any);
    };

    // Only show active subscriptions (filter out cancelled, expired, etc.)
    const activeSubscriptions = subscriptions.filter(sub => {
        const state = (sub.state || '').toString().toUpperCase();
        return state !== 'CANCELLED' && state !== 'EXPIRED' && state !== 'REJECTED';
    });

    const filteredSubscriptions = activeSubscriptions.filter(sub =>
        (sub.displayName || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="subscription-manager">
            {/* Header — always visible */}
            <div className="subscription-manager-header">
                <h1 className="subscription-manager-title">
                    {t('subscriptions.subscriptionsApiKey')}
                </h1>
                <hr className="section-title-divider" />
                <h2 className="subscription-title">{t('subscriptions.newSubscription')}</h2>
            </div>

            {/* Create Form — always visible */}
            <CreateSubscriptionForm
                products={products}
                onCreate={handleCreateSubscription}
                subscriptionLimit={subscriptionLimit}
                currentCount={activeSubscriptions.length}
            />

            {/* Subscription List Section — always visible */}
            <div className="subscription-list-section">
                <p className="subscription-list-title">{t('subscriptions.manageSubscriptionsTitle')}</p>

                {/* Loading indicator (inline, not replacing section) */}
                {loading && subscriptions.length === 0 && (
                    <div className="loading-state" style={{ padding: '20px', textAlign: 'center' }}>
                        <div className="loader-spinner"></div>
                        <p>{t('subscriptions.loadingSubscriptions')}</p>
                    </div>
                )}

                {/* Inline error with retry (not replacing section) */}
                {error && subscriptions.length === 0 && !loading && (
                    <div className="subscription-manager-error" style={{ padding: '16px', textAlign: 'center' }}>
                        <p style={{ color: '#ef4444', marginBottom: '8px' }}>{error}</p>
                        <button onClick={handleRetry} className="retry-button">
                            {t('subscriptions.retry', { defaultValue: 'Retry' })}
                        </button>
                    </div>
                )}

                {/* Subscription cards */}
                {filteredSubscriptions.length > 0 && (
                    <div className="subscription-list">
                        {filteredSubscriptions.map((subscription) => (
                            <SubscriptionCard
                                key={subscription.id}
                                subscription={subscription}
                                onCancel={handleCancelSubscription}
                                onRename={handleRenameSubscription}
                                onRegenerateKey={handleRegenerateKey}
                            />
                        ))}
                    </div>
                )}

                {/* Empty state — only when not loading and no error */}
                {!loading && !error && activeSubscriptions.length === 0 && (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                        <p>{t('subscriptions.noSubscriptions', { defaultValue: 'No active subscriptions. Create one above.' })}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionManager;
